import { MouseEvent, useEffect, useRef } from 'react'
import { YouTubePlayer } from 'react-youtube'
import classNames from 'classnames'
import gsap from 'gsap'
import {} from 'lenis'

type YoutubeVideoTimelineProps = Partial<{
  video: YouTubePlayer
  isPlaying: boolean
  onClickPause: () => void
}>

function mapRange(
  num: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number
) {
  return ((num - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin
}

const YoutubeVideoTimeline = ({
  video,
  onClickPause
}: YoutubeVideoTimelineProps) => {
  const getRoundedProgress = (progress: number) => Number(progress.toFixed(4))

  const getPercentageFromCurrentTime = ({
    duration,
    currentTime
  }: {
    duration: number
    currentTime: number
  }) => mapRange(currentTime, 0, duration, 0, 1)

  const duration = useRef(video.getDuration() || 0)
  const $progress = useRef<HTMLDivElement>(null!)
  const $wrapper = useRef<HTMLDivElement>(null!)

  useEffect(() => {
    const tick = () => {
      let progress = getPercentageFromCurrentTime({
        currentTime: video.getCurrentTime(),
        duration: duration.current
      })
      progress = getRoundedProgress(progress)
      $progress.current.style.transform = `scaleX(${progress})`
    }

    gsap.ticker.add(tick)

    return () => {
      gsap.ticker.remove(tick)
    }
  })

  const onProgressMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    const bounds = $wrapper.current.getBoundingClientRect()
    const left = event.clientX - bounds.left
    let currentTime = mapRange(left, 0, bounds.width, 0, duration.current)
    currentTime = getRoundedProgress(currentTime)
    gsap.fromTo(
      $progress.current,
      {
        scaleX: getPercentageFromCurrentTime({
          currentTime: video.getCurrentTime(),
          duration: duration.current
        })
      },
      {
        scaleX: getPercentageFromCurrentTime({
          currentTime,
          duration: duration.current
        }),
        ease: 'expo.inOut',
        duration: 0.8,
        onComplete: () => {
          video.seekTo(currentTime)
        }
      }
    )
  }

  return (
    <div className="flex h-full items-end">
      <button
        className={classNames('absolute left-0 top-0 h-full w-full')}
        onClick={onClickPause}
      >
        <span
          className={classNames(
            'absolute bottom-[2.5rem] left-[3rem] flex h-[2rem] w-[2rem] cursor-pointer items-center justify-center text-[0rem]',
            'before:mr-[.5rem] before:inline-block before:h-[1.6rem] before:w-[.4rem] before:bg-white',
            'after:mr-[.5rem] after:inline-block after:h-[1.6rem] after:w-[.4rem] after:bg-white'
          )}
        >
          pause
        </span>
      </button>
      <div
        ref={$wrapper}
        onMouseDown={onProgressMouseDown}
        role="presentation"
        className={classNames(
          'relative ml-[3rem] mr-[3rem] h-[.1rem] flex-1 cursor-pointer p-[1.5rem_0]',
          'before:absolute before:top-[1.5rem] before:h-[.1rem] before:w-full before:bg-white/30'
        )}
      >
        <div
          ref={$progress}
          className="absolute left-0 top-[1.5rem] h-[.1rem] w-full origin-left bg-white"
        />
      </div>
    </div>
  )
}

export default YoutubeVideoTimeline
